import type { ViewTypes } from 'src/helper/shared-types';

/**
 * View types that follow the `/v/l/:viewId` route pattern and require view
 * details to be loaded before showing list content. Other view types
 * (users, vendors, roles, tags, work-queues) are "sentinel" pages with a
 * static route and a single fixed config seeded by the boot prefetch.
 */
export const FETCH_DETAILS_VIEW_TYPES: ReadonlyArray<ViewTypes> = [
  'service-requests',
  'work-orders',
  'calendar',
  'quotes',
];

export const isFetchDetailsViewType = (viewType: ViewTypes): boolean =>
  FETCH_DETAILS_VIEW_TYPES.includes(viewType);

/**
 * View types whose lists apply the global market filter (`state.list_view.selectedMarkets`),
 * mirroring MARKETS_ROUTES in src/components/Shared/MarketplaceDropdown/types.ts. Clearing
 * filters on one of these also clears the shared market selection (ORI-496); clearing on any
 * other view type must leave the market selection untouched.
 */
export const MARKET_SCOPED_VIEW_TYPES: ReadonlyArray<ViewTypes> = [
  'work-orders',
  'service-requests',
  'quotes',
  'properties',
  'vendors',
];

export const isMarketScopedViewType = (viewType: ViewTypes): boolean =>
  MARKET_SCOPED_VIEW_TYPES.includes(viewType);

/**
 * View types that persist an ad-hoc Group By selection in the browser URL
 * (`?group=<field>`) so it survives a page refresh (ORI-697). Redux stays the
 * source of truth; the URL only seeds it on load via useGroupByUrlSync. Scoped to
 * the three list views the ticket covers — Vendors/Users grouping is untouched.
 */
export const GROUP_URL_PERSIST_VIEW_TYPES: ReadonlyArray<ViewTypes> = [
  'work-orders',
  'service-requests',
  'quotes',
];

/**
 * Pure helper that folds a Group By selection into an existing query string,
 * preserving every other param. Returns the encoded string WITHOUT a leading `?`.
 * A real value sets `group=<value>`; `none`/absent/empty removes it entirely.
 */
export const buildGroupBySearch = (currentSearch: string, value?: string): string => {
  const params = new URLSearchParams(currentSearch);
  if (value && value !== 'none') {
    params.set('group', value);
  } else {
    params.delete('group');
  }
  return params.toString();
};
